import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wheat, TrendingUp, Users, FileText, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F8F4] via-[#EEE8DC] to-[#F9F8F4]">
      <div className="grain-texture">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="flex justify-center mb-6">
                <div className="bg-[#2E4F3E] p-4 rounded-full shadow-lg">
                  <Wheat className="w-12 h-12 text-[#F9F8F4]" />
                </div>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#2E4F3E] mb-6 tracking-tight">
                Farm Succession
                <span className="block text-[#C87941]">Planning Tool</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#5A6B5E] max-w-3xl mx-auto mb-8 leading-relaxed">
                Build your legacy with comprehensive succession planning. Gather vital information,
                visualize your farm's financial health, and generate professional reports.
              </p>
              <Button
                onClick={() => navigate('/create')}
                size="lg"
                className="bg-[#C87941] hover:bg-[#B06835] text-white px-8 py-6 text-lg rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                data-testid="start-planning-btn"
              >
                Start Planning
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20"
            >
              <Card className="border-2 border-[#2E4F3E]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-[#C87941] smooth-transition bg-white">
                <CardContent className="p-6">
                  <div className="bg-[#2E4F3E]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <FileText className="w-7 h-7 text-[#2E4F3E]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2E4F3E] mb-3">Comprehensive Forms</h3>
                  <p className="text-[#5A6B5E] leading-relaxed">
                    Step-by-step questionnaire covering all aspects of your farm business,
                    from assets to succession planning.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#2E4F3E]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-[#C87941] smooth-transition bg-white">
                <CardContent className="p-6">
                  <div className="bg-[#C87941]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <TrendingUp className="w-7 h-7 text-[#C87941]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2E4F3E] mb-3">Visual Analytics</h3>
                  <p className="text-[#5A6B5E] leading-relaxed">
                    Dynamic charts and graphs displaying financial breakdowns, asset distribution,
                    and succession timelines.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#2E4F3E]/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-[#C87941] smooth-transition bg-white">
                <CardContent className="p-6">
                  <div className="bg-[#2E4F3E]/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-7 h-7 text-[#2E4F3E]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2E4F3E] mb-3">Professional Reports</h3>
                  <p className="text-[#5A6B5E] leading-relaxed">
                    Generate downloadable PDF reports perfect for sharing with advisors,
                    family members, and financial institutions.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="max-w-4xl mx-auto bg-gradient-to-r from-[#2E4F3E] to-[#3A6350] rounded-3xl p-12 shadow-2xl text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Secure Your Farm's Future?
            </h2>
            <p className="text-lg text-[#F9F8F4]/90 mb-8">
              Start your succession planning journey today with our easy-to-use tool.
            </p>
            <Button
              onClick={() => navigate('/create')}
              size="lg"
              className="bg-[#C87941] hover:bg-[#B06835] text-white px-8 py-6 text-lg rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              data-testid="get-started-btn"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;